import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Sidebar from "@/components/Sidebar";

// Full-screen native "Open chart" experience. Embeds the designer's OneChart v245
// prototype (public/onechart.html) in a same-origin iframe, injects the proxy base
// + the logged-in session token, and drives it straight to the selected patient's
// chart — bypassing the prototype's own sign-in / search gate.
const PROXY_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/proxy`;

const esc = (x) =>
  String(x == null ? "" : x).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const pick = (o, keys) => {
  for (const k of keys) {
    const v = o && o[k];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return "";
};
const mask4 = (s) => (s && s.length > 4 ? "····" + s.slice(-4) : s || "");
const PAYER_NAME_KEYS = ["payer_name", "plan_name", "carrier_name", "carrier", "insurance_name", "name", "payer"];
const MEMBER_KEYS = ["member_id", "policy_number", "subscriber_id"];
const SEQ_KEYS = ["priority", "rank", "payer_rank", "sequence"];

// The designer prototype's payer pick-list misses the API's real `carrier_name`
// field, so the banner reads "no payer on file" and the Finance coverage card keeps
// its BCBS fixture. Rebind both from the real chart_payers payload — honest, no
// invented eligibility ("verified today"/"Eligible ✓" don't exist in the API).
function correctCoverage(win, D) {
  const doc = win.document;
  const pay = (D && D.chart_payers) || [];
  const prim = pay.find((p) => /^(1|primary|p)$/i.test(pick(p, SEQ_KEYS))) || pay[0];

  const ppay = doc.querySelector(".rail .pat .ppay");
  if (ppay) {
    if (prim) {
      const pn = pick(prim, PAYER_NAME_KEYS) || "Unnamed payer";
      ppay.innerHTML = esc(pn) + (pay.length > 1 ? ` <span style="color:var(--faint)">+${pay.length - 1} more</span>` : "");
    } else {
      ppay.innerHTML = '<span style="color:var(--mute)">no payer on file</span>';
    }
  }

  const slist = doc.querySelector("#body-sec-coverage .slist");
  if (slist) {
    if (pay.length) {
      slist.innerHTML = pay
        .map((p, i) => {
          const nm = pick(p, PAYER_NAME_KEYS) || "Unnamed payer";
          const mid = pick(p, MEMBER_KEYS);
          const eff = pick(p, ["effective_date", "effective"]);
          const seq = pick(p, SEQ_KEYS) || "—";
          const grp = i === 0 ? "Primary" : i === 1 ? "Secondary" : "Payer " + (i + 1);
          const d1 = esc(nm) + (mid ? ` — member ID ${esc(mask4(mid))}` : "");
          const d2 = esc([eff ? "effective " + eff : "", "on file from chart record · no eligibility check in this API"].filter(Boolean).join(" · "));
          return `<div class="tdiv">${grp}</div><div class="row"><span class="when">${esc(seq)}</span><span class="type">Plan</span><span class="desc"><span class="d1">${d1}</span><span class="d2">${d2}</span></span><span class="st"></span></div>`;
        })
        .join("");
    } else {
      slist.innerHTML = '<div class="tdiv">Primary</div><div class="row"><span class="desc"><span class="d1" style="color:var(--mute)">No payer on file</span></span></div>';
    }
  }
  // Drop the fixture's "Verified today" freshness claim (no eligibility API exists).
  const fresh = doc.querySelector("#sec-coverage .shead .fresh");
  if (fresh) fresh.textContent = "From chart record";
}

// Fixture sweep (user-approved 1a+2a+3a): the designer prototype ships rich sample
// content (the "Maria Garcia" cast: Dr. Chen, M. Torres, Enable Family Medicine PC,
// Non-destructive payload shim: this client's eRx feed uses import-artifact keys
// (drug_sig, rx_start_date, rx_end_date, effective_rx_end_date) and carries NO
// `status`, so the locked meds adapter files every drug as inactive. Map the real
// keys onto the keys the adapter expects (fill only when absent) so Active/Ended,
// sig and duration resolve correctly — without touching the designer's file.
function normalizeErx(D) {
  const arr = D && D.erx;
  if (!Array.isArray(arr)) return;
  const today = new Date().toISOString().slice(0, 10);
  arr.forEach((m) => {
    if (!m || typeof m !== "object") return;
    if (m.sig == null && m.drug_sig != null) m.sig = m.drug_sig;
    if (m.end_date == null) m.end_date = m.effective_rx_end_date || m.rx_end_date || null;
    if (m.start_date == null) m.start_date = m.rx_start_date || null;
    if (m.status == null) {
      const end = m.end_date;
      m.status = !end || String(end).slice(0, 10) >= today ? "Active" : "Discontinued";
    }
  });
}


// BCBS PPO, etc.) in surfaces the API can't fill. Make sure nothing demo reads as real.
function markSampleSurfaces(win, D) {
  const doc = win.document;
  // (1a) AI Chart Summary — force the honest empty state (fixture narrative → "no summary").
  try { if (typeof win.sumLive === "function") win.sumLive(); } catch (e) {}
  // (2a) The legacy `.pdmenu` (fixed-position window/year pull-downs) is positioned
  // ONLY by the build's scroll handler — never on open — so it flashes at the corner
  // and snaps under its button only once you scroll. Run the same positioning math on
  // open. Scoped to `.pdmenu` only; `.pdmenu6`/`.eyrpop` anchor correctly on their own.
  try {
    if (!win.__revqPdFix) {
      win.__revqPdFix = true;
      const place = () => {
        doc.querySelectorAll(".pdmenu.open").forEach((m) => {
          const btn = m.__btn || (m.parentElement && m.parentElement.querySelector(".pdbtn"));
          if (!btn) return;
          const r = btn.getBoundingClientRect();
          if (!r.width) return;
          m.style.top = r.bottom + 6 + "px";
          const w0 = m.offsetWidth || 246;
          let left = r.right - w0;
          if (left < 8) left = 8;
          m.style.left = left + "px";
        });
      };
      doc.addEventListener("click", () => { win.requestAnimationFrame(place); setTimeout(place, 20); }, true);
    }
  } catch (e) {}
  // (2b) Pinned navigator: keep #p-clinical's scroll-snap padding equal to the
  // sticky strip height so a tile-jump lands the section just below the strip.
  try {
    const setSnapPad = () => {
      const idx = doc.querySelector("#p-clinical .cnav#secidx");
      const pane = doc.querySelector("#p-clinical");
      if (idx && pane) pane.style.scrollPaddingTop = idx.offsetHeight + 8 + "px";
    };
    setSnapPad();
    if (!win.__revqSnapPad) {
      win.__revqSnapPad = true;
      doc.addEventListener("click", () => setTimeout(setSnapPad, 0), true);
      win.addEventListener("resize", setSnapPad);
    }
  } catch (e) {}
  // (5) The Lumina posture/mode toggle shows a "0" count badge ("Lumina0") when
  // there are no suggestions — hide any .lc count badge that reads 0/empty.
  try {
    const hideZero = () => {
      doc.querySelectorAll(".lc").forEach((el) => {
        const t = (el.textContent || "").trim();
        el.style.display = t === "" || t === "0" ? "none" : "";
      });
    };
    hideZero();
    if (!win.__revqZeroLc) {
      win.__revqZeroLc = true;
      doc.addEventListener("click", () => setTimeout(hideZero, 0), true);
    }
  } catch (e) {}
  // (6) Rail Problems — render ICD-10 codes with their decimal (E1165 → E11.65).
  try {
    const dot = () => doc.querySelectorAll(".rail .dxc .dxcode6").forEach((el) => {
      const t = (el.textContent || "").trim();
      if (/^[A-Za-z]\d{2}\d+$/.test(t)) el.textContent = t.slice(0, 3) + "." + t.slice(3);
    });
    dot();
    if (!win.__revqIcdDot) { win.__revqIcdDot = true; doc.addEventListener("click", () => setTimeout(dot, 0), true); }
  } catch (e) {}
  // Past medical history — real `history_medical` from the bound payload, exposed
  // to the vendor History card's new Medical lens (read-only; no write API exists).
  try { win.EHI_PMH = (D && D.history_medical) || []; } catch (e) {}
  try { win.EHI_PROBCODES = ((D && D.problems) || []).map(function (p) { return String(p.icd10_code || "").replace(/\./g, "").toUpperCase(); }).filter(Boolean); } catch (e) {}
  // The History card defaults to the Medical lens, which reads EHI_PMH; if the card
  // first painted before this bind, refresh it now that the real data is present.
  try { if (typeof win.renderHist11 === "function") win.renderHist11(); } catch (e) {}
  // (7) Swap the Clinical-tab Encounters card for the v5 timeline (nested iframe for
  // full style isolation), fed the patient's real encounters via a postMessage handshake.
  const buildEnc5Rows = () => {
    const rows = [];
    const iso = (v) => { if (v == null) return null; const t = typeof v === "number" ? v : Date.parse(v); return isFinite(t) ? new Date(t).toISOString() : null; };
    // (a) Encounters → office / tele / cc / call. Rx & lab events are NOT derived
    // from encounters here — the real eRx and lab feeds (below) drive those lanes.
    const srcArr = (win.EHI_ENCTL && (win.EHI_ENCTL._src || win.EHI_ENCTL)) || (D && D.encounters) || [];
    (srcArr || []).forEach((e) => {
      const t = String(e.encounter_type || e.tov_type || e.encounter_class_value || e.tyn || e.ty || e.type || e.reason || "").toLowerCase();
      let cls = "office";
      if (/tele|virtual|video/.test(t)) cls = "tele";
      else if (/phone|telephone|call|text|message|portal/.test(t)) cls = "call";
      else if (/apcm|ccm|chronic|care plan|transitional|coordinat/.test(t)) cls = "cc";
      const dd = iso(e.ts || e.encounter_date || e.date_of_service || e.dos || e.date || e.d);
      if (!dd) return;
      rows.push({
        date: dd, cls,
        // The EHI encounter feed carries no appointment / walk-in signal, so a
        // walk-in flag would be invented. Leave it false (honest).
        walkIn: false,
        title: e.reason || e.chief_complaint || e.encounter_type || e.tyn || e.title || "",
        provider: e.attending_name || e.provider || e.provider_name || e.rendering_provider || "",
        location: e.service_location || e.location || e.facility || "",
      });
    });
    // (b) Fold a feed up by calendar date into ONE marker carrying its names + count.
    const rollup = (arr, dateKeys, nameKeys, cls, unit) => {
      const byDate = {};
      (arr || []).forEach((r) => {
        let d = ""; for (const k of dateKeys) { if (r && r[k]) { d = String(r[k]).slice(0, 10); break; } }
        if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return;
        let nm = ""; for (const k of nameKeys) { if (r && r[k]) { nm = String(r[k]).trim(); break; } }
        (byDate[d] = byDate[d] || []).push(nm || unit);
      });
      Object.keys(byDate).forEach((d) => {
        const names = byDate[d];
        const uniq = [...new Set(names)];
        rows.push({
          date: new Date(d + "T00:00:00").toISOString(), cls, walkIn: false,
          title: uniq.length === 1 ? uniq[0] : uniq.length + " " + unit,
          items: uniq.slice(0, 8), count: uniq.length, provider: "", location: "",
        });
      });
    };
    rollup(D && D.erx, ["rx_sent_date", "rx_start_date", "effective_rx_end_date", "rx_end_date"], ["drug_name", "drug", "medication_name"], "rx", "prescriptions");
    rollup(D && D.elabs, ["result_date", "collected_date", "date"], ["result_code_description", "test_name", "loinc_description", "ai_study_name"], "lab", "results");
    return rows;
  };
  // Per-test numeric trend from the real elabs series → { testName: [{t,v}, …] }.
  // Powers the tiny sparklines in a lab marker's detail popover.
  const buildLabTrends = () => {
    const out = {};
    ((D && D.elabs) || []).forEach((r) => {
      if (!r) return;
      const name = r.result_code_description || r.test_name || r.loinc_description;
      const d = r.result_date || r.collected_date || r.date;
      if (!name || !d) return;
      const v = parseFloat(String(r.result_value == null ? "" : r.result_value).replace(/[^0-9.\-]/g, ""));
      const t = Date.parse(String(d).slice(0, 10));
      if (!isFinite(v) || !isFinite(t)) return;
      (out[name] = out[name] || []).push({ t, v });
    });
    Object.keys(out).forEach((k) => {
      out[k].sort((a, b) => a.t - b.t);
      if (out[k].length < 2) delete out[k];
      else out[k] = out[k].slice(-24);
    });
    return out;
  };
  try {
    if (!win.__revqEnc5Msg) {
      win.__revqEnc5Msg = true;
      win.addEventListener("message", (ev) => {
        const d = ev.data;
        if (d === "enc5:ready" && ev.source) {
          try { ev.source.postMessage({ type: "enc5:data", rows: buildEnc5Rows(), labTrends: buildLabTrends() }, "*"); } catch (e) {}
        } else if (d && d.type === "enc5:h") {
          try {
            const fr = doc.querySelector('#enc5-wrap iframe[data-testid="enc5-frame"]');
            if (fr && d.h) fr.style.height = Math.max(360, d.h) + "px";
          } catch (e) {}
        } else if (d && d.type === "enc5:carry" && d.carry) {
          // a past encounter carried from the timeline → the chart's real engine
          try { win.carryForward(d.carry.id, d.carry.text, d.carry.src, d.carry.target, d.carry.how); } catch (e) {}
        }
      });
    }
  } catch (e) {}
  const mountEnc5 = () => {
    try {
      if (doc.getElementById("enc5-wrap")) return; // mount once (remounts if vendor wipes it)
      const pane = doc.getElementById("p-engagement");
      if (!pane) return; // Engagement tab not rendered yet
      const list = pane.querySelector(".slist") || pane;
      const wrap = doc.createElement("div");
      wrap.id = "enc5-wrap";
      wrap.style.cssText = "margin:0 0 14px";
      const frame = doc.createElement("iframe");
      frame.src = "/encounters_timeline_v5.html";
      frame.setAttribute("data-testid", "enc5-frame");
      frame.style.cssText = "width:100%;height:420px;border:0;display:block;background:transparent";
      wrap.appendChild(frame);
      // The Echo booker (card 8) sits ABOVE the timeline: drop the timeline right
      // after the booker if it is already mounted, otherwise at the top.
      const echoW = doc.getElementById("echo-wrap");
      if (echoW && echoW.parentNode === list) list.insertBefore(wrap, echoW.nextSibling);
      else list.insertBefore(wrap, list.firstChild);
    } catch (e) {}
  };
  mountEnc5();
  try {
    if (!win.__revqEnc5Obs) {
      win.__revqEnc5Obs = true;
      let raf = 0;
      const obs = new win.MutationObserver(() => { win.cancelAnimationFrame(raf); raf = win.requestAnimationFrame(mountEnc5); });
      obs.observe(doc.documentElement, { childList: true, subtree: true });
    }
  } catch (e) {}

  // (8) Echo appointment booker — FIRST card in Engagement (above the v5 timeline).
  // Manual booking is REAL (POST /api/edi/televisit/appointments through the proxy).
  // The AI-assisted path has no upstream and is stamped needs-review / coming-with-Echo.
  const iso10 = (v) => (v ? String(v).slice(0, 10) : "");
  const buildEchoData = async () => {
    const c = (D && D.chart) || {};
    const clientId = c.client_id || "";
    const token = sessionStorage.getItem("revq.token") || "";
    const hdr = { Authorization: `Bearer ${token}` };
    let providers = [], visitTypes = [];
    try {
      const r = await fetch(`${PROXY_BASE}/api/edi/note-to-claim/providers`, { headers: hdr });
      const j = await r.json();
      providers = (j.providers || [])
        .filter((p) => String(p.client_id) === String(clientId))
        .map((p) => ({ npi: p.npi_number, name: p.provider_name, taxonomy: ((p.taxonomies || []).find((t) => t.primary) || (p.taxonomies || [])[0] || {}).code || "" }));
    } catch (e) {}
    try {
      const r = await fetch(`${PROXY_BASE}/api/edi/televisit/visit-types?client_id=${encodeURIComponent(clientId)}`, { headers: hdr });
      const j = await r.json();
      visitTypes = (j.visit_types || []).map((v) => ({ id: v.id ?? v.visit_type_id, name: v.name ?? v.visit_type_name }));
    } catch (e) {}
    const appts = (D && D.appointments) || [];
    const today = new Date().toISOString().slice(0, 10);
    const isFuture = (a) => a.time_category === "future" || (iso10(a.appointment_date) && iso10(a.appointment_date) >= today);
    const upcoming = appts.filter(isFuture)
      .sort((a, b) => (iso10(a.appointment_date) < iso10(b.appointment_date) ? -1 : 1))
      .map((a) => ({ date: iso10(a.appointment_date), physician: a.physician || "" }));
    const past = appts.filter((a) => !isFuture(a) && iso10(a.appointment_date));
    const noShowCount = past.filter((a) => a.is_no_show).length;
    const noShow = past.length ? noShowCount / past.length : 0;
    const pastSorted = past.slice().sort((a, b) => (iso10(a.appointment_date) > iso10(b.appointment_date) ? -1 : 1));
    const lastProv = (pastSorted[0] && pastSorted[0].physician) || "";
    const nm = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
    return {
      patient: {
        name: nm, age: c.age, sex: c.sex, mrn: c.mrn || c.chart_id,
        dob: c.dob, gender: c.sex, clientId,
        phone: c.cell || c.home_phone || c.phone || "", email: c.email || "",
      },
      providers, visitTypes, upcoming,
      noShow, noShowCount, pastCount: past.length,
      lastProv, lastLoc: c.service_location || "",
      lastVisit: iso10((D && D.last_visit_date) || c.last_visit_date),
    };
  };
  const doBook = async (req) => {
    const c = (D && D.chart) || {};
    const token = sessionStorage.getItem("revq.token") || "";
    const nm = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
    const payload = {
      client_id: String(c.client_id || ""),
      npi: req.npi,
      patient_name: nm,
      scheduled_at: req.scheduled_at,
      duration_minutes: Number(req.duration_minutes) || 30,
      reason: req.reason || "Telemedicine visit",
      provider_name: req.provider_name,
      taxonomy_code: req.taxonomy_code || undefined,
      patient_dob: c.dob || undefined,
      patient_gender: c.sex || undefined,
      patient_phone: c.cell || c.home_phone || c.phone || undefined,
      patient_email: c.email || undefined,
      ...(req.visit_type_id ? { visit_type_id: req.visit_type_id, visit_type_name: req.visit_type_name } : {}),
    };
    const r = await fetch(`${PROXY_BASE}/api/edi/televisit/appointments`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) {
      const msg = j.detail || (r.status === 409 ? "That time is already booked (double-booking). Pick another." : "Booking failed.");
      throw new Error(typeof msg === "string" ? msg : "Booking failed.");
    }
    const path = j.patient_link_path || (j.appointment && j.appointment.patient_link_path);
    const inviteUrl = path ? (path.startsWith("http") ? path : `https://ehi.ariaone.ai${path}`) : "";
    return { inviteUrl };
  };
  try {
    if (!win.__revqEchoMsg) {
      win.__revqEchoMsg = true;
      win.addEventListener("message", async (ev) => {
        const d = ev.data;
        if (d === "echo:ready" && ev.source) {
          try { ev.source.postMessage({ type: "echo:data", data: await buildEchoData() }, "*"); } catch (e) {}
        } else if (d && d.type === "echo:book" && ev.source) {
          try { const res = await doBook(d.req || {}); ev.source.postMessage({ type: "echo:booked", ...res }, "*"); }
          catch (e) { ev.source.postMessage({ type: "echo:bookerror", message: (e && e.message) || "Booking failed." }, "*"); }
        } else if (d && d.type === "echo:h") {
          try {
            const fr = doc.querySelector('#echo-wrap iframe[data-testid="echo-frame"]');
            if (fr && d.h) fr.style.height = Math.max(320, d.h) + "px";
          } catch (e) {}
        }
      });
    }
  } catch (e) {}
  const mountEchoBooker = () => {
    try {
      if (doc.getElementById("echo-wrap")) return;
      const pane = doc.getElementById("p-engagement");
      if (!pane) return;
      const list = pane.querySelector(".slist") || pane;
      const wrap = doc.createElement("div");
      wrap.id = "echo-wrap";
      wrap.style.cssText = "margin:0 0 14px";
      const frame = doc.createElement("iframe");
      frame.src = "/echo_booker_v3.html";
      frame.setAttribute("data-testid", "echo-frame");
      frame.style.cssText = "width:100%;height:560px;border:0;display:block;background:transparent";
      wrap.appendChild(frame);
      list.insertBefore(wrap, list.firstChild); // always the very first card
    } catch (e) {}
  };
  mountEchoBooker();
  try {
    if (!win.__revqEchoObs) {
      win.__revqEchoObs = true;
      let raf2 = 0;
      const obs2 = new win.MutationObserver(() => { win.cancelAnimationFrame(raf2); raf2 = win.requestAnimationFrame(mountEchoBooker); });
      obs2.observe(doc.documentElement, { childList: true, subtree: true });
    }
  } catch (e) {}

  // (9) Focus reconciliation — a MOCK "recent arrivals" lane (labs / documents /
  // messages) the artifact shows, stamped "needs review" because the EHI Bridge API
  // has no arrivals feed. Rows carry forward through the vendor's real engine.
  const ARR_MOCK = [
    { id: "fxa-lab1", kind: "Lab", ic: "\u25c9", win: "1 d", t: "Potassium 5.2 mmol/L (H)", s: "CMP \u00b7 above the reference ceiling", target: "Objective", carry: "Potassium 5.2 mmol/L (H), collected yesterday." },
    { id: "fxa-lab2", kind: "Lab", ic: "\u25c9", win: "3 d", t: "eGFR 58 mL/min", s: "down from 64 three months ago", target: "Objective", carry: "eGFR 58 mL/min, down from 64 three months ago." },
    { id: "fxa-doc1", kind: "Document", ic: "\u25a4", win: "2 d", t: "Cardiology letter \u2014 2 pages", s: "outside record \u00b7 read the document before citing it", target: "Assessment", carry: "Cardiology consult letter received (2 pages) \u2014 see document." },
    { id: "fxa-msg1", kind: "Message", ic: "\u2709", win: "4 d", t: "Portal message \u00b7 patient", s: "\u201cran out of the water pill about two weeks ago\u201d \u00b7 unanswered", target: "Subjective", carry: "Patient reports running out of the diuretic about two weeks ago." },
    { id: "fxa-lab3", kind: "Lab", ic: "\u25c9", win: "40 d", t: "HbA1c 8.1%", s: "above target \u00b7 last value in March", target: "Assessment", carry: "HbA1c 8.1%, above target (last value March)." },
  ];
  const arrDays = (w) => { const m = String(w).match(/^(\d+)\s*(d|mo)$/); return m ? (m[2] === "mo" ? +m[1] * 30 : +m[1]) : 9999; };
  const mountFocusArrivals = () => {
    try {
      if (doc.getElementById("fx-arr")) return;
      const pane = doc.getElementById("p-focus");
      if (!pane) return;
      const anchor = doc.getElementById("lastvz");
      const card = doc.createElement("div");
      card.id = "fx-arr";
      card.className = "fzone";
      card.style.cssText = "position:relative";
      const tag = '<span style="margin-left:8px;font-size:8px;font-weight:800;letter-spacing:.05em;text-transform:uppercase;color:#9A5A00;background:#FEF7EC;border:1px solid #F6DFC0;border-radius:999px;padding:2px 7px;vertical-align:1px">needs review</span>';
      const winbar = ["visit", "30", "all"].map((k) => `<button data-fxwin="${k}" class="fxwb" style="font-size:9px;font-weight:700;color:#6C6C69;background:#fff;border:1px solid #E6E6E4;border-radius:999px;padding:3px 10px;cursor:pointer">${k === "visit" ? "Since last visit" : k === "30" ? "30 days" : "All"}</button>`).join("");
      const rows = ARR_MOCK.map((a) => `<div class="fxrow" data-age="${arrDays(a.win)}" style="display:flex;align-items:center;gap:11px;padding:9px 15px;border-top:1px solid #FCFCFB;position:relative">
        <span style="flex:none;font-size:13px;color:#9B9B97">${a.ic}</span>
        <span style="flex:1;min-width:0">
          <span style="display:block;font-size:12px;font-weight:700">${a.t}<span style="font-size:8px;font-weight:800;letter-spacing:.05em;text-transform:uppercase;color:#9B9B97;margin-left:7px">${a.kind}</span></span>
          <span style="display:block;font-size:10px;color:#6E6E6B;margin-top:2px">${a.s}</span></span>
        <span style="flex:none;font-size:9px;color:#9B9B97">${a.win} ago</span>
        <button class="rowcf fxcf" data-id="${a.id}" style="opacity:1">\u29c9 Carry forward</button></div>`).join("");
      card.innerHTML = `<div class="fzh"><h3>Recent arrivals${tag}</h3>
          <span class="fzs">labs, documents and messages since your last look</span>
          <span class="fxwin" style="margin-left:auto;display:flex;gap:6px;align-items:center">${winbar}<span class="cfarrhost" data-testid="carry-menu-arrivals" style="margin-left:2px;display:inline-flex"></span></span></div>
        <div id="fxrows">${rows}</div>
        <div class="cfhint" style="border-top:1px solid #FCFCFB">Mock placeholder \u2014 the EHI Bridge API has no arrivals feed yet. Hover a row to carry it forward into the note.</div>`;
      if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(card, anchor.nextSibling);
      else pane.insertBefore(card, pane.firstChild);
      // v261 · Carry ▾ menu (CarryMenu spec v1) — "In view" scope respects the active window filter
      const arrHost = card.querySelector(".cfarrhost");
      const arrCarryVisible = () => {
        let n = 0;
        card.querySelectorAll(".fxrow").forEach((r) => {
          if (r.style.display === "none") return;
          const b = r.querySelector(".fxcf");
          if (!b || b.getAttribute("disabled") != null) return;
          const a = ARR_MOCK.find((x) => x.id === b.getAttribute("data-id"));
          let ok = true;
          try { ok = win.carryForward(a.id, a.carry, "Recent arrivals \u00b7 arrived " + a.win + " ago", a.target, "carried from what arrived"); } catch (e) {}
          if (ok !== false) { b.setAttribute("disabled", ""); b.textContent = "\u29c9 Carried"; n++; }
        });
        return n;
      };
      const arrVisibleCount = () => [...card.querySelectorAll(".fxrow")].filter((r) => r.style.display !== "none" && r.querySelector(".fxcf") && r.querySelector(".fxcf").getAttribute("disabled") == null).length;
      let arrMenu = null;
      if (arrHost && win.cfMenu) { arrMenu = win.cfMenu([{ label: "In view", sub: "the arrivals shown for this window", amber: false, count: arrVisibleCount, carry: arrCarryVisible }]); arrHost.appendChild(arrMenu); }
      const arrAllUpd = () => { if (arrMenu && arrMenu.__cfRefresh) arrMenu.__cfRefresh(); };
      // window filter
      const applyWin = (k) => {
        card.querySelectorAll(".fxwb").forEach((b) => { b.style.background = b.getAttribute("data-fxwin") === k ? "#141412" : "#fff"; b.style.color = b.getAttribute("data-fxwin") === k ? "#fff" : "#6C6C69"; });
        const cap = k === "visit" ? 14 : k === "30" ? 30 : 9999;
        card.querySelectorAll(".fxrow").forEach((r) => { r.style.display = Number(r.getAttribute("data-age")) <= cap ? "flex" : "none"; });
        arrAllUpd();
      };
      card.querySelectorAll(".fxwb").forEach((b) => b.addEventListener("click", () => applyWin(b.getAttribute("data-fxwin"))));
      applyWin("30");
      // carry forward through the vendor engine
      card.querySelectorAll(".fxcf").forEach((btn) => {
        const a = ARR_MOCK.find((x) => x.id === btn.getAttribute("data-id"));
        if (win.CARRIED_IDS && win.CARRIED_IDS[a.id]) { btn.setAttribute("disabled", ""); btn.textContent = "\u29c9 Carried"; }
        btn.addEventListener("click", (ev) => {
          ev.stopPropagation();
          if (btn.getAttribute("disabled") != null) return;
          let ok = true;
          try { ok = win.carryForward(a.id, a.carry, "Recent arrivals \u00b7 arrived " + a.win + " ago", a.target, "carried from what arrived"); } catch (e) {}
          if (ok !== false) { btn.setAttribute("disabled", ""); btn.textContent = "\u29c9 Carried"; arrAllUpd(); }
        });
      });
      // Last visit card converts to the same Carry ▾ menu
      try { if (win.lvMountCarry) win.lvMountCarry(); } catch (e) {}
    } catch (e) {}
  };
  mountFocusArrivals();
  try {
    if (!win.__revqFxArrObs) {
      win.__revqFxArrObs = true;
      let raf3 = 0;
      const obs3 = new win.MutationObserver(() => { win.cancelAnimationFrame(raf3); raf3 = win.requestAnimationFrame(mountFocusArrivals); });
      obs3.observe(doc.documentElement, { childList: true, subtree: true });
    }
  } catch (e) {}
}

// Move the vendor "+ Sticky note" launcher out of its fixed bottom-left corner and
// into the chart action row, just left of "Start encounter" (its onclick=stkNew is
// preserved by moving the live node). The Ask bar there is hidden via CSS.
function relocateSticky(win) {
  try {
    const doc = win.document;
    const stk = doc.getElementById("stklaunch");
    const start = doc.getElementById("startenc");
    if (!stk || !start || !start.parentNode) return;
    if (stk.parentNode === start.parentNode && stk.nextElementSibling === start) return;
    start.parentNode.insertBefore(stk, start);
  } catch (e) {}
}

// Robust, re-render-proof disclosure stamps for the fixture-heavy surfaces.
const SAMPLE_STAMP_CSS = `
#scendock,#ehibadge{display:none!important;}
/* Match the chart page background to the dashboard's super-light gray (#f5f5f7)
   instead of the vendor's warmer #EFEEEC. --canvas drives the body page bg and the
   sticky clinical-nav / Lumina surfaces below, so they all follow in one move. */
:root{--canvas:#f5f5f7!important;}
body{background:#f5f5f7!important;}
/* (#2/#spacing) Lumina "running in revQ" + lifecycle strip: align its left edge
   with the rail content (14px), give the dots symmetric breathing room, and keep
   the ~5% taller band. */
#lumtab{padding:8.5px 14px!important;}
#lumtab #lumrail{margin-left:auto!important;padding-left:10px!important;}
/* Match the attached target: lifecycle sits flat on the strip (no pill), dots
   come first, then the stage label, then the "N of M" counter. */
#lumtab #lumrail .lcb{background:transparent!important;border:none!important;padding:0!important;gap:12px!important;}
#lumtab #lumrail .lcb:hover{background:transparent!important;}
#lumtab #lumrail .lcrail{order:1!important;gap:6px!important;}
#lumtab #lumrail .lcnow{order:2!important;color:#fff!important;font-weight:800!important;}
#lumtab #lumrail .lcof,#lumtab #lumrail .lccount{order:3!important;color:rgba(255,255,255,.5)!important;}
#lumtab #lumrail .lcdot{background:rgba(255,255,255,.30)!important;}
#lumtab #lumrail .lcdot.done{background:#A79BF5!important;}
#lumtab #lumrail .lcdot.cur{width:9px!important;height:9px!important;background:#fff!important;box-shadow:0 0 0 2.5px rgba(167,155,245,.55)!important;}
/* The black "+ Start encounter" button went invisible on hover (dark-on-dark).
   Flip hover to a white pill with black text + a soft gradient drop shadow. */
.startenc:hover{background:#fff!important;color:#141412!important;border:1px solid #E0DFDB!important;box-shadow:0 10px 26px -10px rgba(20,20,18,.30),0 2px 7px -3px rgba(20,20,18,.16)!important;}
.startenc:hover .plus{opacity:1!important;}
/* Sticky-note relocation: drop the "Ask this chart" field bar from the action row
   and give the "+ Sticky note" launcher a permanent home there instead, styled as a
   clean pill to sit just left of Start encounter (see relocateSticky in JS). */
.askbtn{display:none!important;}
#stklaunch{position:static!important;left:auto!important;bottom:auto!important;top:auto!important;right:auto!important;z-index:auto!important;
  height:30px!important;padding:0 14px!important;border-radius:999px!important;
  border:1px solid var(--hair)!important;background:rgba(255,255,255,.9)!important;color:var(--faint)!important;
  font-size:var(--t3)!important;font-weight:600!important;box-shadow:none!important;
  margin-left:auto!important;margin-right:8px!important;align-items:center!important;gap:6px!important;
  transition:border-color .14s,color .14s,box-shadow .14s!important;}
#stklaunch:hover{color:var(--sub)!important;border-color:#C9C7C1!important;background:rgba(255,255,255,.9)!important;box-shadow:0 1px 3px rgba(20,20,18,.06)!important;}
#stklaunch .plus{font-size:var(--tg)!important;font-weight:400!important;line-height:1!important;opacity:.75!important;}
/* (#3) Drop the Last Visit explainer footer line (per Dan). */
#lastvz .lvfoot,.lvfoot{display:none!important;}
/* Replace the prototype's mock host sidebar with the real revQ sidebar (rendered
   outside the iframe): hide #hostnav and reclaim its grid column. */
#hostnav{display:none!important;}
.shell{grid-template-columns:302px minmax(0,1fr)!important;padding-left:16px!important;}
body.charting .shell,body.docked .shell{grid-template-columns:302px minmax(0,1fr)!important;}
/* (#3) Clinical nav tile strip (.cnav) — v279 renders this transparent and
   NON-sticky by design (it scrolls away; the per-section sort bars are the pinned
   layer). Re-pinning it here creates a second top:0 layer that collides with those
   sort bars, so we leave v279's native behaviour: tiles sit on the page ground. */
/* Focus pane cards now stretch full width to match the Clinical cards (was 22px inset). */
#p-focus{padding-left:0!important;padding-right:0!important;}
/* Safety net only — v279 anchors these menus natively; keep the wrappers as
   positioning contexts so the absolute menus open directly under their button. */
.sv6wrap,.eyrwrap{position:relative!important;}
#comp::before{
  content:"Sample workflow — this note & encounter builder is a demo. It is not wired to this patient's chart and nothing here is saved.";
  position:sticky;top:0;left:0;right:0;z-index:99999;display:block;
  background:#7c2d12;color:#fff;font:600 12.5px/1.5 Inter,system-ui,sans-serif;
  padding:9px 18px;text-align:center;letter-spacing:.01em;
}
#p-engagement.on::before{
  content:"Sample engagement timeline — the EHI Bridge API has no messaging, task or journey feed. Names and notes below are illustrative, not this patient's record.";
  display:block;margin:0 0 14px;padding:10px 14px;border:1px dashed #b45309;border-radius:10px;
  background:#fff7ed;color:#9a3412;font:600 12px/1.5 Inter,system-ui,sans-serif;
}
/* ═══ revQ Chart Polish spec §2 — "Last visit" card spacing (scoped to #lastvz only) ═══
   One repeating row unit with a hairline between rows, a single body padding, and three
   things aligned: the header baseline, the mark against the first text line, and the age
   column's right edge. Card sits on the grey canvas with a hairline and no shadow. */
#lastvz{border:1px solid #E0DFDB!important;border-radius:10px!important;box-shadow:none!important;margin-bottom:14px!important;background:#FFFFFF!important;}
#lastvz .fzh{display:flex!important;align-items:baseline!important;gap:10px!important;padding:14px 16px 0!important;border-bottom:none!important;background:transparent!important;}
#lastvz .fzh h3{margin:0!important;font-size:13px!important;font-weight:700!important;letter-spacing:-.01em!important;min-width:0!important;overflow:hidden!important;text-overflow:ellipsis!important;white-space:nowrap!important;}
#lastvz .fzs{font-size:10.5px!important;color:#6E6E6B!important;min-width:0!important;overflow:hidden!important;text-overflow:ellipsis!important;white-space:nowrap!important;}
#lastvz .fzw{margin-left:auto!important;flex:0 0 auto!important;padding-left:8px!important;font-size:10.5px!important;color:#6E6E6B!important;white-space:nowrap!important;}
#lastvz .lvrows{padding:12px 16px 16px!important;}
#lastvz .lvr{grid-template-columns:18px 1fr auto auto!important;gap:10px!important;padding:10px 0!important;border-top:1px solid #EAE9E6!important;}
#lastvz .lvr:first-child{border-top:none!important;padding-top:2px!important;}
#lastvz .mk3{flex:0 0 18px!important;width:18px!important;height:18px!important;margin-top:1px!important;font-size:10.5px!important;font-weight:700!important;}
#lastvz .lvt{line-height:1.4!important;}
#lastvz .lvo{margin-top:2px!important;line-height:1.45!important;}
#lastvz .lvage{padding-left:10px!important;text-align:right!important;font-variant-numeric:tabular-nums!important;}
/* ═══ Rail · Problems card cleanup (design blueprint) ═══
   The 2-col card grid crams long diagnosis names into a 2-line clamp with a
   confusing "…" affordance. Give each problem a full-width single row: name on
   one clean line (native ellipsis + the card's own hover detail popover), the
   ICD code demoted to a subtle muted chip, onset meta flush right. */
.rail.u9 .dxg2{grid-template-columns:1fr 1fr!important;gap:6px!important;}
.rail.u9 .dxc{display:block!important;overflow:visible!important;}
.rail.u9 .dxc .nm{display:-webkit-box!important;-webkit-line-clamp:2!important;-webkit-box-orient:vertical!important;white-space:normal!important;overflow:hidden!important;line-height:1.3!important;}
.rail.u9 .dxc .nm .more6{display:none!important;}
.rail.u9 .dxc .dxcode6{display:inline-block!important;vertical-align:middle!important;margin:3px 0 0!important;font-size:8.5px!important;font-weight:600!important;letter-spacing:0!important;color:#6C6C69!important;background:#EFEEEC!important;border-radius:4px!important;padding:2px 5px!important;font-variant-numeric:tabular-nums!important;}
.rail.u9 .dxc .meta6{display:block!important;margin-top:2px!important;font-size:10.5px!important;color:#6E6E6B!important;}
/* Allergy pills sit flush to the rail edge — inset them to match the program pills. */
.rail .algpills{padding:0 16px!important;margin:10px 0 2px!important;}
/* The pill under the patient's name already carries allergies — hide the redundant
   rail Allergies section. Keep the node in the DOM: the binder rewrites this
   template by its h4 text, so removing it would blank the rail on live bind. */
#rsec-alg{display:none!important;}
/* ═══ Clinical navigator tiles — pinned (sticky) on the page ground ═══
   The strip is #secidx (first child of the #p-clinical scroll container). Pin it
   with an opaque page-coloured background so it stays put when a tile is clicked;
   scroll-padding-top is set to the strip height in JS so jumped-to sections land
   just below it (no overlap). */
#p-clinical .cnav#secidx{position:sticky!important;top:0!important;z-index:80!important;background:var(--canvas)!important;padding-bottom:10px!important;margin-top:0!important;}
/* ═══ Keep the patient rail while the Lumina centre panel is open (Start encounter) ═══ */
body.lumon .rail,body.lumon .railcol{opacity:1!important;transform:none!important;pointer-events:auto!important;}
body.lumon .shell{grid-template-columns:302px minmax(0,1fr)!important;}
/* ═══ Lumina centre panel — white cards on the #EFEEEC page, same surface as the clinical tab (no light-gray container) ═══ */
/* Hide zero-count badges (e.g. the "Lumina0" on the Chart/Lumina toggle) — a 0
   count reads as noise; CSS is re-render-proof where the JS pass is not. */
.lc.zero{display:none!important;}
/* Encounters card is moved out of Clinical — the v5 timeline is now mounted as the
   first card in the Engagement tab (#enc5-wrap). Hide the vendor Clinical card. */
#sec-encounters{display:none!important;}
#lumpane .lbody,#lumhead{background:var(--canvas)!important;}
/* One-shot "populate" reveal — when the chart binds, the rail sections and Focus
   cards fade-and-rise in a slight stagger so the data reads as filling in organically
   (the payload actually lands all at once). Class is added on ready, removed ~1.3s later. */
@keyframes revqRise{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
body.revq-reveal .rail .pat,body.revq-reveal .rail .algpills,body.revq-reveal .rail .ptags,body.revq-reveal .rail .rsec,body.revq-reveal #p-focus .fzone,body.revq-reveal .pane.on .sect{animation:revqRise .5s cubic-bezier(.4,0,.2,1) both;}
body.revq-reveal .rail .pat{animation-delay:0s}
body.revq-reveal .rail .algpills{animation-delay:.04s}
body.revq-reveal .rail .ptags{animation-delay:.08s}
body.revq-reveal .rail .rsec:nth-of-type(1){animation-delay:.12s}
body.revq-reveal .rail .rsec:nth-of-type(2){animation-delay:.18s}
body.revq-reveal .rail .rsec:nth-of-type(3){animation-delay:.24s}
body.revq-reveal .rail .rsec:nth-of-type(n+4){animation-delay:.30s}
body.revq-reveal #p-focus .fzone:nth-of-type(1){animation-delay:.14s}
body.revq-reveal #p-focus .fzone:nth-of-type(2){animation-delay:.22s}
body.revq-reveal #p-focus .fzone:nth-of-type(3){animation-delay:.30s}
body.revq-reveal #p-focus .fzone:nth-of-type(n+4){animation-delay:.36s}
@media (prefers-reduced-motion: reduce){body.revq-reveal .rail .pat,body.revq-reveal .rail .algpills,body.revq-reveal .rail .ptags,body.revq-reveal .rail .rsec,body.revq-reveal #p-focus .fzone,body.revq-reveal .pane.on .sect{animation:none!important;}}
`;




export default function Chart({ uuid: uuidProp, embedded = false, program: programProp, onChartingChange, targetTab } = {}) {
  const [params] = useSearchParams();
  const uuid = uuidProp || params.get("uuid") || params.get("chart_uuid") || "";
  const program = (programProp || params.get("program") || "APCM").toUpperCase();
  const tabReq = targetTab || (params.get("tab") ? { tab: params.get("tab"), n: 1 } : null);
  const iframeRef = useRef(null);
  const prefetchRef = useRef(null);
  const lastChartingRef = useRef(false);
  const onChartingChangeRef = useRef(onChartingChange);
  const [state, setState] = useState("loading"); // loading | ready | error
  const [errMsg, setErrMsg] = useState("");
  const [patientName, setPatientName] = useState("");
  const [skeletonVisible, setSkeletonVisible] = useState(true);
  const [skeletonFade, setSkeletonFade] = useState(false);
  const mountTsRef = useRef(Date.now());

  useEffect(() => { onChartingChangeRef.current = onChartingChange; });

  // Poll the embedded chart's body for the `charting` class (set by "Start
  // encounter") and lift it to the shell so the revQ sidebar collapses to icons.
  // Polling is used deliberately — a cross-frame MutationObserver proved flaky.
  useEffect(() => {
    if (state !== "ready") return undefined;
    const read = () => {
      try {
        const b = iframeRef.current && iframeRef.current.contentWindow && iframeRef.current.contentWindow.document.body;
        const on = !!(b && b.classList.contains("charting"));
        if (on !== lastChartingRef.current) { lastChartingRef.current = on; onChartingChangeRef.current && onChartingChangeRef.current(on); }
      } catch (e) {}
    };
    read();
    const id = setInterval(read, 400);
    return () => clearInterval(id);
  }, [state]);

  // Release the collapse only when this chart truly unmounts (tab closed / left).
  useEffect(() => () => { try { onChartingChangeRef.current && onChartingChangeRef.current(false); } catch (e) {} }, []);

  // Land on a requested chart tab (Finance / Engagement) when the row action asked
  // for a destination other than Focus. Retries until the tab chip exists and the
  // chart has bound; works on first mount and when re-activating an open chart.
  const tabDest = tabReq ? tabReq.tab : null;
  const tabNonce = tabReq ? tabReq.n : null;
  useEffect(() => {
    if (!tabDest || tabDest === "focus") return undefined;
    let tries = 0;
    const id = setInterval(() => {
      tries += 1;
      try {
        const doc = iframeRef.current && iframeRef.current.contentWindow && iframeRef.current.contentWindow.document;
        const pane = doc && doc.getElementById(`p-${tabDest}`);
        const chip = doc && doc.querySelector(`.chip[data-p="${tabDest}"]`);
        if (pane && pane.classList.contains("on")) { clearInterval(id); return; } // landed
        if (chip) chip.click(); // keep asking until the pane is actually active
      } catch (e) {}
      if (tries > 140) clearInterval(id);
    }, 150);
    return () => clearInterval(id);
  }, [tabDest, tabNonce]);

  // Cross-fade the glowing skeleton out once the chart is bound (min 250ms so a
  // fast load never flickers).
  useEffect(() => {
    if (state !== "ready") return undefined;
    const wait = Math.max(0, 250 - (Date.now() - mountTsRef.current));
    const t1 = setTimeout(() => setSkeletonFade(true), wait);
    const t2 = setTimeout(() => setSkeletonVisible(false), wait + 460);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [state]);

  // Kick the (often multi-MB, ~2s upstream) chart fetch off on mount so it runs
  // CONCURRENTLY with the iframe download/parse instead of serially after it.
  useEffect(() => {
    document.title = "Patient Chart · revQ";
    const token = sessionStorage.getItem("revq.token") || "";
    if (!uuid || !token) return;
    prefetchRef.current = fetch(`${PROXY_BASE}/api/ehibridge/import/chart-by-id/${encodeURIComponent(uuid)}`, {
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("HTTP " + r.status))))
      .catch(() => null); // fall back to the iframe's own fetch on failure
  }, [uuid]);

  const drive = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const token = sessionStorage.getItem("revq.token") || "";
    let tries = 0;

    const tick = async () => {
      tries += 1;
      let win;
      try {
        win = iframe.contentWindow;
      } catch (e) {
        return;
      }
      const ready = win && typeof win.ehiChart === "function" && typeof win.ehiBind === "function" && win.EHI;
      if (!ready) {
        if (tries < 150) setTimeout(tick, 100);
        else { setErrMsg("The chart failed to initialize."); setState("error"); }
        return;
      }

      if (!uuid) { setErrMsg("No patient chart was specified."); setState("error"); return; }
      if (!token) { setErrMsg("Your session has expired. Please sign in again."); setState("error"); return; }

      // Point the prototype at our proxy + reuse the app session token.
      win.EHI.base = PROXY_BASE;
      win.EHI.token = token;
      win.EHI.program = program;
      // Hide the prototype's internal demo affordances (scenario/"change patient"
      // switcher) so the native chart only ever shows the real patient we opened.
      try {
        const doc = win.document;
        if (!doc.getElementById("revq-chart-embed-css")) {
          const st = doc.createElement("style");
          st.id = "revq-chart-embed-css";
          st.textContent = SAMPLE_STAMP_CSS;
          doc.head.appendChild(st);
        }
      } catch (e) {}
      try {
        // Prefer the data we already started fetching on mount; fall back to the
        // iframe's own fetch if the prefetch failed or wasn't started.
        let D = prefetchRef.current ? await prefetchRef.current : null;
        if (!D) D = await win.ehiChart(uuid);
        try { normalizeErx(D); } catch (e) {}
        win.EHI.uuid = uuid;
        win.ehiBind(D);
        try { correctCoverage(win, D); } catch (e) {}
        try { markSampleSurfaces(win, D); } catch (e) {}
        try { relocateSticky(win); } catch (e) {}
        try {
          const c = (D && D.chart) || {};
          const nm = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
          if (nm) setPatientName(nm.replace(/\b\w/g, (m) => m.toUpperCase()).replace(/\B\w/g, (m) => m.toLowerCase()));
        } catch (e) {}
        const gate = win.document.getElementById("ehigate");
        if (gate) gate.classList.remove("on");
        // One-shot staggered "populate" reveal of the real cards/rail as they appear.
        try {
          const b = win.document.body;
          b.classList.add("revq-reveal");
          setTimeout(() => { try { b.classList.remove("revq-reveal"); } catch (e) {} }, 1300);
        } catch (e) {}
        setState("ready");
      } catch (e) {
        setErrMsg((e && e.message) || "The chart could not be loaded.");
        setState("error");
      }
    };
    tick();
  };

  return (
    <div
      style={
        embedded
          ? { position: "absolute", inset: 0, display: "flex", background: "#f5f5f7" }
          : { position: "fixed", inset: 0, display: "flex", background: "#f5f5f7" }
      }
      data-testid="chart-embed-page"
    >
      {!embedded && <Sidebar activeChart={patientName || undefined} />}
      <div style={{ position: "relative", flex: 1, minWidth: 0, background: "#f5f5f7" }}>
        {state === "error" ? (
          <div
            style={{
              position: "absolute", inset: 0, zIndex: 12, display: "flex",
              alignItems: "center", justifyContent: "center", flexDirection: "column",
              gap: 14, background: "#f5f5f7", color: "#141412",
              fontFamily: "Inter, system-ui, sans-serif",
            }}
            data-testid="chart-embed-error"
          >
            <div style={{ fontSize: 15, fontWeight: 700 }}>Couldn’t open this chart</div>
            <div style={{ fontSize: 13, opacity: 0.7, maxWidth: 420, textAlign: "center", lineHeight: 1.5 }}>{errMsg}</div>
            <button
              onClick={() => { setState("loading"); setErrMsg(""); setSkeletonVisible(true); setSkeletonFade(false); mountTsRef.current = Date.now(); drive(); }}
              data-testid="chart-embed-retry"
              style={{
                marginTop: 6, padding: "8px 16px", borderRadius: 999, border: "1px solid #d8d7d3",
                background: "#141412", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer",
              }}
            >
              Try again
            </button>
          </div>
        ) : (
          skeletonVisible && (
            <div
              className="csk-root"
              style={{ opacity: skeletonFade ? 0 : 1 }}
              data-testid="chart-embed-loading"
              aria-label="Loading patient chart"
            >
              {/* left rail + main */}
              <div className="csk-body">
                {/* left rail — real labels, only the data shimmers */}
                <div className="csk-rail">
                  <div className="csk-idrow">
                    <div className="sk csk-av" />
                    <div className="csk-idlines"><div className="sk sk-h" /><div className="sk sk-m" /><div className="sk sk-s" /></div>
                  </div>
                  <div className="sk csk-alg" />
                  <div className="csk-pills">{["APCM", "CCM", "RPM", "AWV"].map((p, i) => <span key={p} className={`csk-pill-real${i === 0 ? " on" : ""}`}>{p}</span>)}</div>
                  <div className="csk-sec">
                    <div className="csk-sechd"><span className="csk-lbl">Problems</span><span className="csk-cnt">39 active</span></div>
                    <div className="csk-segs"><span className="csk-seg on">List</span><span className="csk-seg">By system</span></div>
                    <div className="csk-sublbl">Active chronic · 11</div>
                    <div className="csk-pgrid">
                      {[0, 1, 2, 3, 4, 5].map((i) => (
                        <div className="csk-pcard" key={i}>
                          <div className="sk sk-line" style={{ width: "94%" }} />
                          <div className="csk-pmeta"><span className="sk csk-code" /><div className="sk sk-line" style={{ width: "38%" }} /></div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="csk-sec">
                    <div className="csk-sechd"><span className="csk-lbl">Vitals — Jun 10</span><span className="csk-edit">Edit</span></div>
                    <div className="csk-vgrid">
                      {["BP", "HR", "WT", "HT", "BMI"].map((v) => (
                        <div className="csk-vt" key={v}><span className="csk-vlbl">{v}</span><div className="sk sk-vval" /></div>
                      ))}
                    </div>
                  </div>
                  <div className="csk-sec">
                    <div className="csk-sechd"><span className="csk-lbl">Visit history</span><span className="csk-cnt">82</span></div>
                    <div className="csk-bars">{[64, 40, 52, 34, 46, 28, 58, 38].map((h, i) => <div className="sk csk-bar" key={i} style={{ height: h }} />)}</div>
                  </div>
                </div>
                {/* main — real view tabs + card titles, only the data shimmers */}
                <div className="csk-main">
                  <div className="csk-tabs">
                    <div className="csk-tabl">
                      {[["Focus"], ["Clinical", "4"], ["Engagements, Visits & Medical documents"], ["Finance"], ["Record", "1"]].map(([t, n], i) => (
                        <span key={t} className={`csk-tab-real${i === 0 ? " on" : ""}`}>{t}{n ? <span className="csk-tbadge">{n}</span> : null}</span>
                      ))}
                    </div>
                    <div className="csk-tabr">
                      <span className="csk-ask">Ask this chart ⌘K</span>
                      <span className="csk-startbtn">Start encounter</span>
                    </div>
                  </div>
                  {[
                    { t: "Last visit — what we said, and what happened", sub: "12 Jun 2025 · Annual physical", badge: "3 of 5 still open", check: true },
                    { t: "Patient actions", badge: "needs review", rows: 1 },
                    { t: "Direct messages · 2", badge: "needs review", rows: 1 },
                    { t: "Since you last looked", badge: "needs review", rows: 1 },
                    { t: "Visit history · 5 encounters", badge: "needs review", rows: 1 },
                    { t: "Preventive & care gaps", badge: "needs review", rows: 1 },
                    { t: "Care plan — chronic care management", badge: "partial", rows: 2 },
                  ].map((card, c) => (
                    <div className="csk-card" key={c}>
                      <div className="csk-chd">
                        <div className="csk-ctitle">{card.t}{card.sub ? <span className="csk-csub">{card.sub}</span> : null}</div>
                        {card.badge ? <span className="csk-cbadge">{card.badge}</span> : null}
                      </div>
                      {card.check
                        ? [0, 1, 2, 3, 4].map((r) => (
                            <div className="csk-chkrow" key={r}><span className="sk csk-chk" /><div className="sk sk-line" style={{ width: `${[70, 82, 64, 76, 58][r]}%` }} /></div>
                          ))
                        : [...Array(card.rows)].map((_, r) => <div className="sk sk-line" key={r} style={{ width: r === 0 ? "88%" : "62%" }} />)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )
        )}
        <iframe
          ref={iframeRef}
          src="/onechart.html"
          title="Patient chart"
          onLoad={drive}
          data-testid="chart-embed-frame"
          style={{ width: "100%", height: "100%", border: 0, display: "block" }}
        />
      </div>
      <style>{`
        .csk-root{position:absolute;inset:0;z-index:10;display:flex;flex-direction:column;background:#f5f5f7;transition:opacity .46s ease;overflow:hidden;font-family:Inter,system-ui,sans-serif}
        .csk-strip{flex:none;height:27px;background:#141412;display:flex;align-items:center;gap:9px;padding:0 15px}
        .csk-lumtab{display:flex;align-items:center;gap:8px;font-size:10px;font-weight:700;color:#EDEDEA}
        .csk-lumdot{width:9px;height:9px;border-radius:50%;background:linear-gradient(135deg,#A79BF5,#6B5CE7)}
        .csk-lumtail{color:#B9B8B4;font-weight:500}
        .csk-sdots{margin-left:auto;display:flex;align-items:center;gap:5px}
        .csk-sdot{width:5px;height:5px;border-radius:50%;background:rgba(255,255,255,.28)}
        .csk-sdot.cur{width:8px;height:8px;background:#fff;box-shadow:0 0 0 2px rgba(167,155,245,.5)}
        .csk-body{flex:1;min-height:0;display:flex;overflow:hidden}
        .csk-rail{width:302px;flex:none;background:#fff;border-right:1px solid #E6E6E4;padding:15px;display:flex;flex-direction:column;gap:14px;overflow:hidden}
        .csk-idrow{display:flex;gap:11px;align-items:center}
        .csk-av{width:44px;height:44px;border-radius:10px;flex:none}
        .csk-idlines{flex:1;display:flex;flex-direction:column;gap:6px}
        .csk-alg{height:9px;width:66%;border-radius:999px}
        .csk-pills{display:flex;gap:6px;flex-wrap:wrap}
        .csk-pill-real{font-size:10px;font-weight:600;color:#6C6C69;border:1px solid #E6E6E4;border-radius:999px;padding:3px 10px;line-height:1.4}
        .csk-pill-real.on{color:#141412;border-color:#CFCEC9}
        .csk-sec{display:flex;flex-direction:column;gap:8px;padding-top:12px;border-top:1px solid #F0EFEC}
        .csk-sechd{display:flex;align-items:baseline;gap:8px}
        .csk-lbl{font-size:9px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#8A8A87}
        .csk-cnt{font-size:9px;color:#B7B7B3}
        .csk-edit{margin-left:auto;font-size:9px;font-weight:600;color:#9B9B97}
        .csk-segs{display:flex;gap:5px}
        .csk-seg{font-size:10px;font-weight:600;color:#9B9B97;border:1px solid #EAE9E6;border-radius:999px;padding:2px 9px}
        .csk-seg.on{color:#141412;background:#F3F2EF;border-color:#E0DFDB}
        .csk-sublbl{font-size:8.5px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#B7B7B3}
        .csk-pgrid{display:grid;grid-template-columns:1fr 1fr;gap:6px}
        .csk-pcard{border:1px solid #EDECE9;border-radius:7px;padding:7px 9px;display:flex;flex-direction:column;gap:6px}
        .csk-pmeta{display:flex;align-items:center;gap:6px}
        .csk-code{width:34px;height:11px;border-radius:3px;flex:none}
        .csk-vgrid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:7px}
        .csk-vt{border:1px solid #EDECE9;border-radius:9px;padding:7px 9px;display:flex;flex-direction:column;gap:6px}
        .csk-vlbl{font-size:8px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:#9B9B97}
        .sk-vval{height:15px;width:64%}
        .csk-bars{display:flex;align-items:flex-end;gap:5px;height:66px;padding-top:4px}
        .csk-bar{flex:1;border-radius:3px 3px 0 0}
        .csk-main{flex:1;min-width:0;padding:12px 22px 40px;display:flex;flex-direction:column;gap:13px;overflow:hidden}
        .csk-tabs{display:flex;align-items:center;gap:22px;padding:2px 0 12px;border-bottom:1px solid #EDECE9}
        .csk-tabl{display:flex;align-items:center;gap:22px}
        .csk-tab-real{display:inline-flex;align-items:center;gap:6px;font-size:13px;font-weight:600;color:#9B9B97;padding-bottom:10px;margin-bottom:-13px;border-bottom:2px solid transparent}
        .csk-tab-real.on{color:#141412;border-bottom-color:#141412}
        .csk-tbadge{font-size:9px;font-weight:700;color:#8A8A87;background:#F0EFEC;border-radius:999px;padding:1px 6px}
        .csk-tabr{margin-left:auto;display:flex;align-items:center;gap:10px}
        .csk-ask{font-size:11px;color:#9B9B97;border:1px solid #E6E6E4;border-radius:8px;padding:6px 12px}
        .csk-startbtn{font-size:11.5px;font-weight:600;color:#fff;background:#141412;border-radius:8px;padding:7px 14px}
        .csk-card{background:#fff;border:1px solid #E6E6E4;border-radius:12px;padding:15px 18px;display:flex;flex-direction:column;gap:9px;animation:cskglow 2.4s ease-in-out infinite}
        .csk-card:nth-of-type(3){animation-delay:.4s}
        .csk-card:nth-of-type(5){animation-delay:.8s}
        .csk-chd{display:flex;align-items:flex-start;gap:10px}
        .csk-ctitle{flex:1;min-width:0;font-size:13px;font-weight:700;color:#141412;display:flex;align-items:baseline;gap:9px;flex-wrap:wrap}
        .csk-csub{font-size:10.5px;font-weight:500;color:#9B9B97}
        .csk-cbadge{flex:none;font-size:8.5px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;color:#9A5A00;background:#FEF7EC;border:1px solid #F0C990;border-radius:999px;padding:2px 8px}
        .csk-chkrow{display:flex;align-items:center;gap:10px}
        .csk-chk{width:14px;height:14px;border-radius:4px;flex:none}
        .sk{background:linear-gradient(90deg,#e9e8e5 25%,#f5f4f1 37%,#e9e8e5 63%);background-size:200% 100%;border-radius:5px;animation:cskshim 1.5s ease-in-out infinite}
        .sk-h{height:13px;width:58%} .sk-m{height:9px;width:86%} .sk-s{height:9px;width:70%}
        .sk-line{height:11px}
        @keyframes cskshim{0%{background-position:200% 0}100%{background-position:-200% 0}}
        @keyframes cskglow{0%,100%{box-shadow:0 0 0 1px rgba(230,230,228,.7)}50%{box-shadow:0 0 18px 0 rgba(107,92,231,.16),0 0 0 1px rgba(107,92,231,.14)}}
        @media (prefers-reduced-motion: reduce){.csk-root .sk,.csk-card{animation:none}}
      `}</style>
    </div>
  );
}
